import type { ReactNode } from 'react';

import { Tooltip } from '../../components/Tooltip.js';

/**
 * One setting: label left, control right (ARCHITECTURE.md §103.2).
 *
 * ==================== WHY NOT A STACKED FORM ====================
 * A widget has fifteen-odd settings and an operator changes two of them. A
 * stacked label-above-input form turns that into a page of scrolling, where a
 * label/control row lets the whole set be scanned at once — which is what
 * makes a settings panel feel like an instrument rather than a questionnaire.
 *
 * Stacked fields are still right where the INPUT needs the width: a name, a
 * currency code, a list of platforms. This is for the rest.
 * ================================================================
 */
export function InspectorRow({
  label,
  help,
  htmlFor,
  stacked = false,
  children,
}: {
  label: string;
  /** Explains WHY, never restates the label (§38 of the redesign brief). */
  help?: string | undefined;
  htmlFor?: string | undefined;
  /**
   * Put the control on its own line.
   *
   * The escape hatch this component's own header describes, now actually
   * available. A `select` whose longest option is "Recording, or generic OBS
   * output" cannot share a 26rem inspector row with its label: the control
   * will not shrink, so the LABEL wraps instead and its help marker ends up
   * jammed against the select's border, reading as though it were inside it.
   *
   * Use it where the input genuinely needs the width, not to escape a tight
   * row — a panel of stacked fields is the questionnaire this component
   * exists to avoid.
   */
  stacked?: boolean;
  children: ReactNode;
}): React.JSX.Element {
  /*
   * ==================== THE ROW IS THE LABEL ====================
   * This used to be a `div` wrapping a `label` that named nothing: the
   * `label` sat BESIDE the control rather than around it, and associated
   * with it only if a caller remembered to pass `htmlFor`. Across 28 uses in
   * `WidgetEditor.tsx`, **not one did.** Every select and number field in the
   * inspector was therefore unlabelled.
   *
   * It was found by the Chat Overlay E2E on its first run:
   * `getByLabel(/shown on/i)` resolved to the help marker's `aria-label`,
   * because the `?` span was the only thing in the row carrying an
   * accessible name. A screen reader reached the same conclusion.
   *
   * Making the row itself a `label` gives IMPLICIT association — the control
   * is inside it, so no id and no caller cooperation is needed. An opt-in
   * that nobody opted into for 28 consecutive call sites is not a mechanism,
   * it is a comment; `htmlFor` stays only for the rare row whose control
   * genuinely lives elsewhere.
   * ==============================================================
   */
  return (
    <label className={`irow${stacked ? ' irow--stacked' : ''}`} htmlFor={htmlFor}>
      <span className="irow__label">
        {label}
        {help !== undefined && (
          <Tooltip tip={help}>
            {/*
              * `aria-hidden`, and no longer focusable.
              *
              * Its `aria-label` was the accessible name the row was missing,
              * so a screen reader announced "About Shown on" where the
              * control's own name belonged. The help text is a Tooltip on
              * hover and focus of the row; duplicating it as a separate tab
              * stop inside a label put a second, wrong name on the control.
              */}
            <span className="irow__help" aria-hidden="true">
              ?
            </span>
          </Tooltip>
        )}
      </span>
      <div className="irow__control">{children}</div>
    </label>
  );
}

/**
 * A switch.
 *
 * A real `input[type=checkbox]` underneath, styled: it keeps the keyboard
 * behaviour, the focus ring and the accessible role that a div with a click
 * handler would have to reimplement badly. `role="switch"` because that is
 * what it looks like and therefore what it should announce.
 */
export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Used when the switch stands alone, without an `InspectorRow` label. */
  label?: string | undefined;
}): React.JSX.Element {
  return (
    <input
      type="checkbox"
      role="switch"
      className="toggle"
      checked={checked}
      {...(label === undefined ? {} : { 'aria-label': label })}
      onChange={(event) => {
        onChange(event.target.checked);
      }}
    />
  );
}

/**
 * A bounded number, with its unit.
 *
 * The unit is rendered rather than implied: "15" beside "Hide after" tells an
 * operator nothing, and a number input whose meaning is seconds should say so.
 */
export function NumberField({
  value,
  min,
  max,
  unit,
  id,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  unit?: string | undefined;
  id?: string | undefined;
  onChange: (next: number) => void;
}): React.JSX.Element {
  return (
    <span className="numfield">
      <input
        type="number"
        className="field__input numfield__input"
        value={value}
        min={min}
        max={max}
        {...(id === undefined ? {} : { id })}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(Math.min(Math.max(next, min), max));
        }}
      />
      {unit !== undefined && <span className="numfield__unit">{unit}</span>}
    </span>
  );
}
